import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { programService } from '../../../services/programService';
import { AddProgramTemplateModal } from '../../organisms/AddProgramTemplateModal';
import { EditProgramTemplateModal } from '../../organisms/EditProgramTemplateModal';

const Container = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #333;
  margin: 0;
`;

const AddButton = styled.button`
  padding: 12px 24px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #0056b3;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TableContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #dee2e6;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f8f9fa;
  }
`;

const TableHeaderCell = styled.th`
  padding: 16px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const TableCell = styled.td`
  padding: 16px;
  font-size: 14px;
  color: #666;
`;

const ActionButton = styled.button`
  padding: 8px 16px;
  background-color: ${props => props.$variant === 'edit' ? '#28a745' : '#dc3545'};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-right: 8px;

  &:hover {
    background-color: ${props => props.$variant === 'edit' ? '#218838' : '#c82333'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingContainer = styled.div`
  padding: 48px;
  text-align: center;
  color: #666;
  font-size: 16px;
`;

const EmptyContainer = styled.div`
  padding: 48px;
  text-align: center;
  color: #999;
  font-size: 16px;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  background-color: ${props => props.$type === 'form' ? '#007bff' : '#28a745'};
  color: white;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  margin-left: 8px;
`;

const ProgramManagementPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const allTemplates = await programService.getAllProgramTemplates();
      setTemplates(allTemplates);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTemplate = async (templateData) => {
    try {
      await programService.createProgramTemplate(templateData);
      await fetchTemplates();
      setIsAddModalOpen(false);
    } catch (error) {
      console.error('Failed to add template:', error);
      throw error;
    }
  };

  const handleEditTemplate = async (templateId, templateData) => {
    try {
      await programService.updateProgramTemplate(templateId, templateData);
      await fetchTemplates();
      setIsEditModalOpen(false);
      setSelectedTemplate(null);
    } catch (error) {
      console.error('Failed to update template:', error);
      throw error;
    }
  };

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setIsEditModalOpen(true);
  };

  return (
    <Container>
      <Header>
        <PageTitle>프로그램 관리</PageTitle>
        <AddButton onClick={() => setIsAddModalOpen(true)} disabled={loading}>
          새 프로그램 템플릿 추가
        </AddButton>
      </Header>

      <TableContainer>
        {loading ? (
          <LoadingContainer>프로그램 템플릿을 불러오는 중...</LoadingContainer>
        ) : templates.length === 0 ? (
          <EmptyContainer>등록된 프로그램 템플릿이 없습니다.</EmptyContainer>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>대주제</TableHeaderCell>
                <TableHeaderCell>중주제</TableHeaderCell>
                <TableHeaderCell>프로그램</TableHeaderCell>
                <TableHeaderCell>반복/성공 기준</TableHeaderCell>
                <TableHeaderCell>행동 유형</TableHeaderCell>
                <TableHeaderCell>완료 기준</TableHeaderCell>
                <TableHeaderCell>작업</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <tbody>
              {templates.map((template) => (
                <TableRow key={template.id}>
                  <TableCell>{template.majorCategory}</TableCell>
                  <TableCell>{template.middleCategory}</TableCell>
                  <TableCell>
                    {template.minorCategory}
                    {template.useFreeForm ? (
                      <Badge $type="markdown">마크다운</Badge>
                    ) : (
                      <Badge $type="form">폼</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {template.repetitionCount}회 / {template.successCriteria}회
                  </TableCell>
                  <TableCell>
                    {template.behaviorType === 'positive_above' ? '긍정행동' : '이상행동'}
                  </TableCell>
                  <TableCell>
                    {template.programSuccessCriteria}회 {template.programSuccessMode === 'consecutive' ? '연속' : '누적'}
                  </TableCell>
                  <TableCell>
                    <ActionButton
                      $variant="edit"
                      onClick={() => handleEdit(template)}
                    >
                      수정
                    </ActionButton>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        )}
      </TableContainer>

      <AddProgramTemplateModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleAddTemplate}
      />

      <EditProgramTemplateModal
        isOpen={isEditModalOpen}
        template={selectedTemplate}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedTemplate(null);
        }}
        onSubmit={handleEditTemplate}
      />
    </Container>
  );
};

export default ProgramManagementPage;
